import "babel-polyfill";
import Vue from "vue";
import VueRouter from "vue-router";
import VeeValidate from "vee-validate";
import VueCalendarHeatmap from "vue-calendar-heatmap";
import VueClipboard	from "vue-clipboard2";
import {
	DateTime,
	Interval,
} from "luxon";
import store from "./store";
import App from "./App.vue";
import router from "./router";
import { commitTypes, actionTypes } from "./store-types";
import vuetify from "./plugins/vuetify";
import { pad } from "./utils";

Vue.use(VeeValidate);
Vue.config.productionTip = false;

Vue.use(VueClipboard);
Vue.use(VueRouter);

Vue.use(VueCalendarHeatmap);

Vue.mixin({
	computed: {
		config() {
			return this.$store.state.config;
		},
		timeZone() {
			return this.$store.state.zone;
		},
		isAdmin() {
			return this.$store.state.role === "Admin";
		},
	},
	methods: {
		dt(val) {
			return DateTime.fromISO(val, { zone: "utc" }).setZone(this.timeZone);
		},
		dtnow() {
			return DateTime.utc().setZone(this.timeZone);
		},
		formatTime(d) {
			const val = DateTime.isDateTime(d) ? d : this.dt(d);
			return d ? val.toLocaleString(DateTime.TIME_24_WITH_SECONDS) : "";
		},
		formatDate(d) {
			const val = DateTime.isDateTime(d) ? d : this.dt(d);
			return d ? val.toLocaleString(DateTime.DATE_SHORT) : "";
		},
		formatDateTime(d) {
			const val = DateTime.isDateTime(d) ? d : this.dt(d);
			return d ? val.toLocaleString(DateTime.DATETIME_SHORT) : "";
		},
		timeDuration(start, end) {
			if (!start || !end) {
				return "";
			}

			const startDt = DateTime.isDateTime(start) ? start : this.dt(start);
			const endDt = DateTime.isDateTime(end) ? end : this.dt(end);
			const diff = Interval.fromDateTimes(startDt, endDt).toDuration().shiftTo("hours", "minutes", "seconds", "milliseconds");
			const hours = diff.hours;
			const min = diff.minutes;

			if (hours === 0 && min === 0) {
				return `<span class="blue-grey--text text--lighten-4">${pad(hours)} H : ${pad(min)} M :</span> ${pad(diff.seconds)} S : ${pad(diff.milliseconds)} mS`;
			}

			if (hours === 0) {
				return `<span class="blue-grey--text text--lighten-4">${pad(hours)} H :</span> ${pad(min)} M : ${pad(diff.seconds)} S : ${pad(diff.milliseconds)} mS`;
			}

			return `${pad(hours)} H : ${pad(min)} M : ${pad(diff.seconds)} S : ${pad(diff.milliseconds)} mS`;
		},
		showToast(color, message) {
			this.$store.commit(commitTypes.TOAST, { color, message });
		},
		showError(err) {
			let msg;
			if (err instanceof Error) {
				msg = err.message;
			}
			if (err.response && err.response.data) {
				msg = `${err.response.statusText} : ${err.response.data}`;
			}
			if (!err.response && err.request) {
				msg = err.request;
			}
			if (err.message) {
				msg = err.message;
			}
			if (!msg) {
				msg = err;
			}
			this.$store.commit(commitTypes.SET_ERROR, msg);
		},
		clearError() {
			this.$store.commit(commitTypes.SET_ERROR, null);
		},
	},
});

store.dispatch(actionTypes.LOAD_CONFIG)
	.then(() => store.dispatch(actionTypes.CHECK_TOKEN))
	.then(() => {
		// navigation guards
		router.beforeEach((to, from, next) => {
			store.commit(commitTypes.SET_ERROR, null);
			// public routes
			if (to.meta.access.includes("*")) {
				next();
				return;
			}

			// private routes without login
			if (!store.state.loggedIn) {
				next({ name: "login" });
				return;
			}

			// USER trying admin routes
			if (store.state.role === "User" && !to.meta.access.includes("User")) {
				next({ name: "login" });
				return;
			}

			// ADMIN or USER on USER route
			next();
		});

		new Vue({
			router,
			store,
			vuetify,
			render: h => h(App),
		}).$mount("#app");
	});
